import { Button, Form, Input, InputNumber, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { WebApiIntegrationsDtoExternalArticleDto } from "../../Repository/eventbookyapi";
import { TextService } from "../../Services/TextService";
import { getFortnoxVatOptions, DEFAULT_FORTNOX_VAT_RATE } from "../../Helpers/FortnoxInvoiceHelpers";

const { Option } = Select;

interface FortnoxInvoiceRow {
    articleNumber?: string;
    description?: string;
    amount?: number;
    vat?: number;
}

interface FortnoxInvoiceRowsEditorProps {
    form: FormInstance;
    /** Form.List field name (default "rows"). */
    name?: string;
    articles: WebApiIntegrationsDtoExternalArticleDto[];
}

/**
 * Multi-line invoice-row editor. One membership fee is the common case, but filtering
 * members by group means a subgroup may need several lines (e.g. membership + section +
 * training fee), so rows can be added/removed freely.
 */
export const FortnoxInvoiceRowsEditor = ({ form, name = "rows", articles }: FortnoxInvoiceRowsEditorProps) => {
    const GetText = TextService.GetText;

    const onArticlePicked = (rowName: number, articleNumber: string | undefined) => {
        const article = articles.find(a => a.articleNumber === articleNumber);
        if (!article) return;
        const rows = form.getFieldValue(name) as FortnoxInvoiceRow[];
        const next = [...rows];
        next[rowName] = {
            ...next[rowName],
            articleNumber,
            description: article.name ?? next[rowName]?.description,
            amount: article.salesPrice ?? next[rowName]?.amount,
            vat: article.vat ?? DEFAULT_FORTNOX_VAT_RATE,
        };
        form.setFieldValue(name, next);
    };

    return (
        <div style={{ maxWidth: 600, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{GetText("FortnoxInvoice_RowsHeading")}</div>
            <Form.List name={name}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name: rowName, ...rest }, idx) => (
                            <div key={key} style={{ border: "1px solid #e8e8e8", borderRadius: 6, marginBottom: 8, background: "#fafafa" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderBottom: "1px solid #e8e8e8" }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{GetText("FortnoxInvoice_RowLabel").replace("{0}", (idx + 1).toString())}</span>
                                    {fields.length > 1 && (
                                        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(rowName)}>
                                            {GetText("FortnoxInvoice_RemoveRow")}
                                        </Button>
                                    )}
                                </div>
                                <div style={{ padding: "10px 12px" }}>
                                    {articles.length > 0 && (
                                        <Form.Item {...rest} name={[rowName, "articleNumber"]} label={GetText("FortnoxInvoice_ArticleLabel")} style={{ marginBottom: 8 }}>
                                            <Select
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                placeholder={GetText("FortnoxInvoice_ArticlePlaceholder")}
                                                onChange={(v) => onArticlePicked(rowName, v)}
                                            >
                                                {articles.map(a => (
                                                    <Option key={a.articleNumber!} value={a.articleNumber!} label={`${a.articleNumber} ${a.name}`}>
                                                        {a.articleNumber} — {a.name}{a.vat != null ? ` (${a.vat}% moms)` : ""}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item {...rest} name={[rowName, "description"]} label={GetText("AddPayment_Description")} rules={[{ required: true, message: GetText("AddPaymentLink_DescriptionRequired") }]} style={{ marginBottom: 8 }}>
                                        <Input placeholder={"T.ex. 'Medlemsavgift 2026'"} />
                                    </Form.Item>
                                    <Form.Item {...rest} name={[rowName, "amount"]} label={GetText("FortnoxInvoice_AmountExclVatLabel")} rules={[{ required: true, message: GetText("AddPaymentLink_AmountRequired") }]} style={{ marginBottom: 8 }}>
                                        <InputNumber min={0} max={1000000} style={{ width: 160 }} />
                                    </Form.Item>
                                    <Form.Item {...rest} name={[rowName, "vat"]} label={GetText("FortnoxInvoice_VatLabel")} style={{ marginBottom: 0 }}>
                                        <Select style={{ width: 160 }} options={getFortnoxVatOptions()} />
                                    </Form.Item>
                                </div>
                            </div>
                        ))}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ vat: DEFAULT_FORTNOX_VAT_RATE })}>
                            {GetText("FortnoxInvoice_AddRow")}
                        </Button>
                    </>
                )}
            </Form.List>
        </div>
    );
};

/** Sum helper for the net/VAT/total line. rows is the Form.List value. */
export const sumFortnoxRows = (rows: FortnoxInvoiceRow[] | undefined) => {
    const list = Array.isArray(rows) ? rows : [];
    let net = 0;
    let vat = 0;
    for (const r of list) {
        const amount = Number(r?.amount) || 0;
        const rate = Number(r?.vat ?? DEFAULT_FORTNOX_VAT_RATE) || 0;
        net += amount;
        vat += Math.round(amount * rate) / 100;
    }
    return { net, vat, gross: net + vat };
};
